/** Ressources i18n — English (structure alignée sur `fr` / `de`). */
export const en = {
  translation: {
    common: {
      loading: 'Loading...',
      error: 'Something went wrong',
      save: 'Save',
      cancel: 'Cancel',
      delete: 'Delete',
      edit: 'Edit',
      add: 'Add',
      confirm: 'Confirm',
      back: 'Back',
      search: 'Search...',
      noResults: 'No results',
      currency: 'CHF',
    },
    nav: {
      dashboard: 'Dashboard',
      orders: 'Orders',
      products: 'Catalog',
      stats: 'Statistics',
      settings: 'Settings',
      support: 'Help & support',
      logout: 'Log out',
      login: 'Log in',
      openMenu: 'Open menu',
      closeMenu: 'Close menu',
      mainNav: 'Main navigation',
    },
    session: {
      timeoutTitle: 'Automatic sign-out',
      timeoutLabel: 'Inactivity timeout',
      timeoutHint: 'After a period of inactivity, you will be signed out automatically.',
      timeoutSaved: 'Timeout saved.',
      timeout15: '15 minutes',
      timeout30: '30 minutes',
      timeout60: '1 hour',
      timeout120: '2 hours',
      timeout240: '4 hours',
      timeoutNever: 'Never (not recommended)',
      inactivityLogout: 'Session expired due to inactivity. Please sign in again.',
    },
    auth: {
      login: 'Log in',
      register: 'Create an account',
      email: 'Email',
      password: 'Password',
      forgotPassword: 'Forgot password?',
      noAccount: 'No account yet?',
      hasAccount: 'Already have an account?',
      demoAccountsTitle: 'Demo accounts:',
      demoAccountsHint: 'Click a row to pre-fill email and password.',
      demoRoleAdmin: 'Administrator',
      demoRoleMerchant: 'Merchant',
      demoRoleDriver: 'Driver',
      demoRoleCustomer: 'Customer',
    },
    dashboard: {
      welcome: 'Hello, {{name}}!',
      todayOrders: 'Orders today',
      todayRevenue: 'Revenue today',
      avgBasket: 'Average basket',
      avgRating: 'Average rating',
      newOrders: 'New orders',
      inProgress: 'In progress',
      shopStatus: 'Shop status',
      open: 'Open',
      closed: 'Closed',
      closeNow: 'Close now',
      topProducts: 'Top products today',
      storefrontPendingTitle: 'Storefront pending approval',
      storefrontPending: 'Your storefront is not online yet. Customers will not see it until BienLivrer validates it.',
      plans: {
        link: 'See plans',
        title: 'Your plans',
        intro: 'Starter is free. Pro and Premium lower the commission and raise the catalog limit. A paid plan starts on the 1st of the following month at 00:00 (Swiss time) and renews every month.',
        free: 'Free',
        perMonth: 'CHF {{price}} / month',
        commission: 'Commission {{rate}}%',
        products: '{{max}} products in the catalog',
        current: 'Current plan',
        choose: 'Choose {{name}}',
        backToStarter: 'Back to Starter',
        keep: 'Keep {{name}}',
        scheduled: 'Switch scheduled',
        cancelScheduled: 'Back to Starter on {{date}} at 00:00 (Swiss time). The paid plan stays active until then.',
        portal: 'Invoices and payment method',
        checkoutUnavailable: 'Online subscription is not open yet. Write to BienLivrer to change plan.',
        demoReadonly: 'The demo account cannot subscribe.',
        success: 'Card saved. The recurring subscription will be active on the 1st of the following month at 00:00 (Swiss time).',
        cancelledCheckout: 'Subscription cancelled. Nothing was charged.',
        catalogOver: 'Your catalog has {{count}} products, above this plan’s limit ({{max}}). Existing products stay online; you cannot add more until the extra ones are archived.',
        trialDefer: 'The first charge happens after your trial ends.',
        trialSoon: 'Your trial ends in less than 48 hours: the monthly charge starts when you subscribe.',
        error: 'The plan cannot be changed right now.',
        confirmTitle: 'Activate a recurring subscription',
        confirmBody: 'You are about to activate the {{name}} plan at CHF {{price}} per month. It will be active from {{date}} at 00:00 (Swiss time). It then renews every month until you return to Starter.',
        confirmYes: 'Activate the subscription',
        confirmNo: 'Cancel',
        confirmCancelTitle: 'Cancel the recurring subscription',
        confirmCancelBody: 'You are cancelling the recurring {{name}} subscription. It stays active until {{date}} at 00:00 (Swiss time). From then you return to Starter and the monthly charge stops.',
        confirmCancelPending: 'You are cancelling the recurring {{name}} subscription. It will not start. The cancellation takes effect on {{date}} at 00:00 (Swiss time).',
        confirmCancelYes: 'Cancel the subscription',
        confirmCancelNo: 'Back',
        pendingStart: '{{name}} will be active on {{date}} at 00:00 (Swiss time). The charge repeats every month.',
        planned: 'Already scheduled',
        names: {
          starter: 'Starter',
          pro: 'Pro',
          premium: 'Premium',
        },
      },
    },
    orders: {
      title: 'Orders',
      number: 'Order no.',
      customer: 'Customer',
      status: 'Status',
      total: 'Total',
      type: 'Type',
      date: 'Date',
      accept: 'Accept',
      refuse: 'Decline',
      markReady: 'Mark ready',
      prepare: 'Prepare',
      confirmPickup: 'Mark picked up',
      delivery: 'Delivery',
      click_collect: 'Pickup',
      statuses: {
        pending: 'Pending',
        accepted: 'Accepted',
        preparing: 'Preparing',
        ready: 'Ready',
        pickup: 'Driver on the way',
        delivering: 'Out for delivery',
        delivered: 'Delivered',
        completed: 'Picked up',
        cancelled: 'Cancelled',
        refunded: 'Refunded',
        pending_payment: 'Payment pending',
      },
    },
    products: {
      title: 'My catalog',
      add: 'New product',
      name: 'Product name',
      nameFr: 'Name (French)',
      nameDe: 'Name (German)',
      nameEn: 'Name (English)',
      price: 'Price CHF',
      stock: 'Stock',
      unlimited: 'Unlimited',
      status: 'Status',
      category: 'Category',
      active: 'Active',
      draft: 'Draft',
      out_of_stock: 'Out of stock',
      allergens: 'Allergens',
      vatRate: 'VAT rate',
    },
    onboarding: {
      title: 'Create your shop',
      step1: 'Basic information',
      step2: 'Opening hours',
      step3: 'Bank details',
      step4: 'First product',
      next: 'Next',
      previous: 'Previous',
      finish: 'Finish',
    },
    client: {
      home: 'Home',
      myOrders: 'My orders',
      myProfile: 'My profile',
      cart: 'Cart',
      addToCart: 'Add to cart',
      addedToCartToast: 'Added to cart',
      viewCart: 'View cart',
      addedToCartLive: '{{name}} added to cart.',
      removeFromCart: 'Remove from cart',
      emptyCart: 'Your cart is empty',
      orderNow: 'Order',
      deliveryAddress: 'Delivery address',
      deliveryOrPickup: 'Delivery or pickup?',
      notes: 'Special instructions (optional)',
      coupon: 'Promo code',
      applyCoupon: 'Apply',
      subtotal: 'Subtotal',
      deliveryFee: 'Delivery fee',
      serviceFee: 'Service fee',
      packagingDeposit: 'Packaging deposit',
      discount: 'Discount',
      total: 'Total',
      placeOrder: 'Place order',
      orderPlaced: 'Order placed!',
      trackOrder: 'Track my order',
      pickupCode: 'Pickup code',
      estimatedTime: 'Estimated time',
      deliveryIn: 'Delivery in ~{{min}} min',
      pickupIn: 'Ready in ~{{min}} min',
      activeOrders: 'Active',
      orderHistory: 'History',
      noActiveOrders: 'No active orders',
      noHistory: 'No past orders',
      checkout: {
        errorGeneric: 'Could not complete the order. Please try again.',
        errorNetwork: 'No connection or server unreachable.',
        errorServer: 'Server error. Please try again shortly.',
        errorValidation: 'Invalid data. Check the form.',
        errorUnauthorized: 'Log in to place an order.',
        submitting: 'Placing order…',
        payCard: 'Online payment',
        payQrBill: 'QR bill',
        payCash: 'Cash on pickup',
        payCashHint:
          'You will pay the full amount (items and service fee) in cash at the counter when you collect your order.',
        payCashDeliveryOnly: 'Pickup in store only',
        payCashLocked:
          'Available from your {{fromOrder}}th completed online order ({{current}}/{{required}}).',
        payCashUnavailableDeposit:
          'Cash is not available: a packaging deposit must be collected online so we can refund it.',
        payCashCta: 'Order and pay on pickup — CHF {{total}}',
        cartDialogTitle: 'Cart',
        cartOpened: 'Cart open. {{count}} item(s). Total {{total}} CHF.',
        cartClosed: 'Cart closed.',
        loginToOrderTitle: 'Login required',
        loginToOrderHint:
          'Log in, create an account, or use the guest form below. Your cart and choices (pickup / delivery, payment) will be kept.',
        loginToOrderCta: 'Log in to order',
        loginToOrderRegister: 'Create an account',
        guestCheckoutToggle: 'Checkout as guest',
        guestCheckoutHint:
          'No account is created. We use these details only to process your order and contact you (confirmation, delivery).',
        guestFirstNameLabel: 'First name',
        guestLastNameLabel: 'Last name',
        guestEmailLabel: 'Email',
        guestPhoneOptionalLabel: 'Phone (optional, +41…)',
        guestPhoneInvalid: 'Invalid phone: use +41 followed by 9 digits.',
        guestSubmitCta: 'Place order as guest',
        guestSubmitting: 'Sending…',
        guestDividerOr: 'or',
        continueToPayment: 'Continue to payment',
        preparePaymentCta: 'Continue to payment — CHF {{total}}',
        orderWithPaymentObligation: 'Order with obligation to pay — CHF {{total}}',
        acceptLegalRequired: 'Please accept the terms of sale and terms of use to continue.',
        paymentNotConfirmedYet:
          'Your order will only be confirmed after successful payment (card or TWINT).',
        backToSummary: 'Back to cart',
        stripePayTitle: 'Secure payment',
        stripePaySubtitle: 'Card — your order stays pending until payment is confirmed.',
        stripePayCta: 'Pay CHF {{total}} — payment obligation',
        stripePayProcessing: 'Processing…',
        awaitingPaymentTitle: 'Order received',
        awaitingPaymentBody:
          'Check your email for next steps (card or TWINT). Track the order under “My orders”.',
        awaitingPaymentBodyGuest:
          'Check your email for next steps (card or TWINT). Keep your order number below.',
        stripePollTimeout: 'Payment may still be confirming. Open “My orders” in a moment.',
        stripeOrderCancelled: 'The order was cancelled (payment failed).',
        wrongRoleHint:
          'You are logged in as a merchant or driver. Log out and use a customer account to order here.',
        sectionAccount: 'Account',
        sectionFulfillment: 'Pickup or delivery',
        sectionYourOrder: 'Your order',
        sectionSummary: 'Summary',
        sectionPayment: 'Payment method',
        signedInAs: 'Signed in as',
        tabHasAccount: 'I already have an account',
        tabGuest: 'Checkout as guest',
        deliveryAddressShort: 'Delivery address',
        totalTvaHint: 'VAT included where applicable',
        paymentChooseHint: 'Choose how you would like to pay.',
        paymentOnlineHint:
          'Secure online payment by card or TWINT (via Stripe). No cash or QR bill.',
        serviceFeeHint: 'Includes secure payment processing.',
      },
      ordersConnection: {
        offline: 'Offline — updates when you reconnect.',
        online: 'Back online.',
        syncing: 'Refreshing orders…',
      },
      profile: {
        title: 'My profile',
        firstNameLabel: 'First name',
        lastNameLabel: 'Last name',
        emailLabel: 'Email',
        phoneLabel: 'Phone',
        firstNameRequired: 'First name required',
        lastNameRequired: 'Last name required',
        emailInvalid: 'Invalid email',
        saveBtn: 'Save',
        saved: 'Profile saved.',
        saveFailed: 'Could not save. Please try again.',
        gdprTitle: 'Personal data (GDPR)',
        gdprExport: 'Export my data',
        gdprDelete: 'Delete my account',
        gdprDeleteConfirm: 'Your account will be anonymised. This cannot be undone. Continue?',
        gdprPasswordLabel: 'Password to confirm deletion',
        gdprPasswordRequired: 'Enter your password to delete the account.',
        gdprRequestSent: 'Your request was sent.',
        changePasswordTitle: 'Change password',
        currentPassword: 'Current password',
        newPassword: 'New password (min. 8 characters)',
        confirmPassword: 'Confirm new password',
        passwordMismatch: 'Passwords do not match',
        passwordChanged: 'Password changed successfully.',
      },
      shop: {
        deliveryBadge: 'Delivery',
        clickCollectBadge: 'Click & Collect',
        pickup: 'In-store pickup',
        deliveryWithFee: 'Delivery (+CHF {{fee}})',
        deliveryFromFee: 'Delivery (CHF {{fee}})',
        deliveryOutOfZone: 'Outside delivery area',
        deliveryOffered: 'Free',
        deliveryOfferedLabel: 'Free delivery',
        categoryAll: 'All',
        products: 'Products',
        noProducts: 'No products available',
        notFound: 'Shop not found',
        orderPlacedTitle: 'Order placed!',
        orderPlacedBody: 'Your payment is confirmed. Your order is being processed.',
        orderNumberLabel: 'Order no.',
        pickupCodeLabel: 'Pickup code',
        newOrder: 'New order',
        viewCartShort: 'View cart',
        cartArticleLabel: 'items',
        closeCartOverlay: 'Close cart',
        closeDialog: 'Close',
        deliveryAddressLabel: 'Delivery address *',
        deliveryAddressPlaceholder: 'Main Street 5, 2500 City',
        orderNoteLabel: 'Note (optional)',
        orderNotePlaceholder: 'Allergies, gluten-free…',
        paymentMethod: 'Payment method',
        onlineOrderPrice: 'Online order price',
        onlineOrderPriceHint: 'Displayed prices are for online orders.',
        packagingDeposit: 'Packaging deposit +CHF {{amount}}',
        packagingDepositHint:
          'Deposit for special packaging (cold chain, meat…). BienLivrer holds it and refunds it when you return the packaging at the shop.',
      },
      homePage: {
        searchPlaceholder: 'Search for a shop...',
        closed: 'Closed',
        open: 'Open',
        demo: 'Demo',
        delivery: 'Delivery',
        pickup: 'Pickup',
        minOrder: 'Min. CHF {{amount}}',
        driverArea: 'Driver area',
        driverProfile: 'Driver profile',
        merchantDashboard: 'Merchant dashboard',
        merchantSettings: 'Shop settings',
        admin: 'Administration',
        myOrders: 'My orders',
        myProfile: 'My profile',
        logout: 'Log out',
        login: 'Log in',
        announcementCta: 'Contact / details',
        brandSlogan: 'Small shop, big-league delivery.',
        heroTitle: 'Local shops, delivered to you',
        heroVision: 'Local delivery across Switzerland',
        heroSubtitle: 'Order from local merchants — evening route delivery or in-store pickup',
        register: 'Create an account',
        loginCta: 'Log in',
        hello: 'Hello, {{name}}!',
        taglineDriver: 'Browse the showcase or manage your deliveries',
        taglineMerchant: 'Showcase or merchant area',
        taglineAdmin: 'Showcase or admin',
        taglineCustomer: 'What would you like to order today?',
        deliveriesLink: 'Deliveries',
        dashboardLink: 'My shop',
        ordersLink: 'My orders',
        featured: 'Featured',
        pinned: "Don't miss",
        pinnedBadge: 'Pick',
        newPartners: 'New partners',
        locationBanner: 'Local shops available near you',
        noResults: 'No results',
        noShops: 'No shops available',
        resetSearch: 'Reset search',
        shopCountOne: '{{count}} shop',
        shopCountMany: '{{count}} shops',
      },
    },
    driver: {
      title: 'My deliveries',
      space: 'Driver area',
      available: 'Available',
      unavailable: 'Unavailable',
      activeDelivery: 'Active delivery',
      availableDeliveries: 'Available jobs',
      earnings: 'Weekly earnings',
      accept: 'Accept job',
      refuse: 'Decline',
      pickup: 'Pick up',
      deliver: 'Deliver',
      delivered: 'Delivery confirmed',
      distance: 'Distance',
      estimatedFee: 'Estimated fee',
      noDeliveries: 'No jobs available',
      status: {
        searching: 'Finding driver',
        awaiting_manual: 'Awaiting manual assignment',
        pending_acceptance: 'Awaiting confirmation',
        assigned: 'Assigned',
        heading_to_shop: 'Heading to shop',
        at_shop: 'At shop',
        picked_up: 'Order picked up',
        delivering: 'Delivering',
        delivered: 'Delivered',
      },
    },
    admin: {
      title: 'Administration',
      console: 'BienLivrer admin console',
      myProfile: 'My profile',
      overview: 'Overview',
      shops: 'Shops',
      users: 'Users',
      orders: 'Orders',
      coupons: 'Coupons',
      reviews: 'Reviews',
      activeShops: 'Active shops',
      totalUsers: 'Users',
      totalOrders: 'Total orders',
      revenue: 'Total revenue (CHF)',
      verify: 'Verify',
      verified: 'Verified',
      active: 'Active',
      inactive: 'Inactive',
      suspended: 'Suspended',
      owner: 'Owner',
      createdAt: 'Created',
      refresh: 'Refresh',
      sessionTimeoutTitle: 'Automatic sign-out',
      sessionTimeoutHint: 'After a period of inactivity on the admin console, you will be signed out automatically.',
      sessionTimeoutSaved: 'Timeout saved.',
      sessionTimeout15: '15 minutes',
      sessionTimeout30: '30 minutes',
      sessionTimeout60: '1 hour',
      sessionTimeout120: '2 hours',
      sessionTimeout240: '4 hours',
      sessionTimeoutNever: 'Never (not recommended)',
      sessionInactivityLogout: 'Session expired due to inactivity. Please sign in again.',
    },
    gdpr: {
      cookieBanner: 'We use cookies to improve your experience.',
      closeBanner: 'Close cookie notice',
      accept: 'Accept',
      decline: 'Decline',
      learnMore: 'Learn more',
      privacyPolicy: 'Privacy policy',
      termsOfService: 'Terms of use',
    },
    errors: {
      notFound: 'Page not found',
      unauthorized: 'Unauthorized',
      serverError: 'Server error',
      networkError: 'Network error — check your connection',
      sessionExpired: 'Session expired — please log in again',
      shopClosed: 'This shop is currently closed',
      outOfStock: 'Out of stock',
      minOrder: 'Minimum order not reached (CHF {{amount}})',
    },
    merchantDemo: {
      bannerTitle: 'Demo environment',
      bannerBody: 'Sample « {{category}} » shop. Accept, prepare, print and follow through to delivery — nothing is saved.',
      changeCategory: 'Change shop type',
      pickerTitle: 'Choose your shop type',
      pickerSubtitle: 'The merchant area adapts to your selection (catalog, orders, stats…).',
      readOnlyHint: 'Interactive walkthrough — to open your real shop, sign up as a partner.',
      persistBlocked: 'Demo environment — no changes saved.',
    },
    driverDemo: {
      bannerTitle: 'Driver demo environment',
      bannerBody: 'Sample jobs, map and earnings. Accept and update statuses — nothing is saved.',
      persistBlocked: 'Demo environment — no changes saved.',
    },
    devTestAccounts: {
      title: 'Test accounts',
      subtitle: 'Local development only. Run seed at project root: ',
      seedCmd: 'npm run db:seed',
      unavailable: 'This page is not available in this environment.',
      backHome: 'Home',
      backLogin: 'Log in',
      columnRole: 'Role',
      columnEmail: 'Email',
      columnPassword: 'Password',
      columnRedirect: 'After login',
      copyEmail: 'Copy email',
      copyPassword: 'Copy password',
      copied: 'Copied',
      openLogin: 'Open login with pre-filled fields',
      roles: {
        admin: 'Administrator',
        merchant: 'Merchant',
        merchantDemo: 'Demo merchant (showcase)',
        merchantE2e: 'Test merchant (E2E)',
        driverDemo: 'Demo driver (interactive)',
        driver: 'Test driver (E2E)',
        customer: 'Customer (catalog)',
      },
      redirects: {
        admin: '/admin',
        merchant: '/dashboard',
        merchantDemo: '/demo/commerce',
        merchantE2e: '/dashboard',
        driver: '/driver',
        customer: '/client',
      },
      shopDemoTitle: 'Demo shop',
      shopDemoSlug: '/shop/boulangerie-muller-biel',
      loginHint: 'From the login page you can also use the shortcuts below the form.',
      loginFullPageLink: 'Open dedicated test accounts page',
      securityNote: 'Never enable test accounts in production; do not commit real secrets.',
    },
  },
};
